import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import type { Tour } from "@noesis/shared";
import { definirEnsaio, emEnsaio, pedir } from "../api.js";
import { PASSOS, depoisDoAlvo, type AlvoDoTour, type DadosDoTour } from "../componentes/tour/passos.js";

/**
 * O tour de primeiro uso (ADR 041).
 *
 * O estado mora no servidor (`tour.ver`/`tour.gravar`, invariante 8): pular no
 * notebook vale no celular, e recarregar a página continua do mesmo passo. A
 * tela escreve otimista e manda gravar sem esperar — o tour é guia, e uma
 * falha de rede não pode travar quem está estudando.
 *
 * **Passo de ação avança no evento real**, não num "próximo": os
 * manipuladores que já existem chamam `avancarSe(alvo)` quando fazem o que o
 * passo pediu (criar a trilha, colar, conferir, confirmar a resposta…). Quem
 * se adianta não fica preso: `avancarSe` procura o alvo do passo atual em
 * diante, e pula o que já foi feito. Se o alvo do passo atual nem está na tela
 * — a pessoa saiu dele, como a conta com questões que foi estudar em vez de
 * importar —, a procura atravessa etapas e o tour segue o que ela fez.
 *
 * **O tour é um ensaio** (decisão do Italo, 2026-09-26): as telas são as de
 * verdade, mas enquanto ele dura o servidor responde de um banco de mentira
 * (`apps/api/src/ensaio.ts`) e nada chega à conta. Os campos se preenchem
 * sozinhos com o exemplo, e nenhum passo depende do que a pessoa produzir fora
 * do app. Entrar e sair do ensaio recarregam a página: é o jeito de nenhuma
 * tela ficar com dado de um mundo mostrado no outro.
 *
 * O tour só aparece no largo — a lateral é parte do que ele mostra. No
 * estreito ele não desenha nada e não avança: sem ensaio, os eventos seriam
 * dos dados reais.
 */

type ValorDoTour = {
  /** O passo em curso, ou `null` quando o tour está parado ou não carregou. */
  passo: number | null;
  /** Já passou por ele (pulou ou terminou). Decide o "refazer o tour". */
  concluido: boolean;
  dados: DadosDoTour;
  avancarSe: (alvo: AlvoDoTour) => void;
  /** O "entendi →" dos passos de informação. */
  entendi: () => void;
  pular: () => void;
  refazer: () => void;
  /** Os números que os textos citam: itens conferidos, meta, quanto falta. */
  informar: (d: Partial<DadosDoTour>) => void;
  /** Conta que nunca viu o tour, no Início: pergunta antes de começar. */
  convite: boolean;
  recusar: () => void;
};

const Contexto = createContext<ValorDoTour | null>(null);

const PARADO: Tour = { passo: -1, concluido: false };

export function TourProvider({ children }: { children: ReactNode }) {
  const [tour, setTour] = useState<Tour | null>(null);
  const [dados, setDados] = useState<DadosDoTour>({});
  const atual = useRef<Tour | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    let vivo = true;
    void pedir("tour.ver")
      .then((t) => {
        if (!vivo) return;
        // A aba e o servidor discordam do ensaio: o tour parou em outro
        // aparelho, ou esta aba é nova num tour em curso. Acerta e recarrega.
        const deveEnsaiar = t.passo >= 0 && ehLargo();
        if (deveEnsaiar !== emEnsaio()) {
          definirEnsaio(deveEnsaiar);
          window.location.reload();
          return;
        }
        atual.current = t;
        setTour(t);
      })
      .catch(() => {
        // Sem o estado do servidor o tour não começa sozinho: melhor não guiar
        // do que guiar de novo quem já pulou.
        if (vivo) setTour(null);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const gravar = useCallback((novo: Tour) => {
    atual.current = novo;
    setTour(novo);
    void pedir("tour.gravar", { corpo: novo }).catch(() => {
      /* otimista: a próxima gravação leva o estado inteiro de novo */
    });
  }, []);

  /** Grava e só então recarrega: a página nova já lê o tour no estado certo. */
  const trocarDeMundo = useCallback(async (novo: Tour) => {
    atual.current = novo;
    setTour(novo);
    try {
      await pedir("tour.gravar", { corpo: novo });
    } catch {
      /* sem servidor: a página nova relê o que ficou gravado */
    }
    definirEnsaio(novo.passo >= 0);
    window.location.assign("/");
  }, []);

  const parar = useCallback(() => void trocarDeMundo({ passo: -1, concluido: true }), [trocarDeMundo]);

  const avancarSe = useCallback(
    (alvo: AlvoDoTour) => {
      const t = atual.current;
      if (!t || t.passo < 0 || !emEnsaio()) return;
      const proximo = depoisDoAlvo(t.passo, alvo, !alvoNaTela(PASSOS[t.passo]?.alvo));
      if (proximo === null) return;
      if (proximo >= PASSOS.length) parar();
      else gravar({ passo: proximo, concluido: t.concluido });
    },
    [gravar, parar],
  );

  const entendi = useCallback(() => {
    const t = atual.current;
    if (!t || t.passo < 0) return;
    const passo = PASSOS[t.passo];
    const fim = !passo || passo.ultimo || t.passo + 1 >= PASSOS.length;
    if (fim) parar();
    else gravar({ passo: t.passo + 1, concluido: t.concluido });
  }, [gravar, parar]);

  /** Começa sempre do passo 0, num ensaio vazio: a conta parece nova. */
  const refazer = useCallback(
    () => void trocarDeMundo({ passo: 0, concluido: atual.current?.concluido ?? false }),
    [trocarDeMundo],
  );

  /*
    Toda conta que nunca viu o tour (sem linha: passo -1, `concluido` falso) é
    **convidada** a fazê-lo, uma vez, no Início e no largo. O tour só começa se
    ela aceitar; recusar grava `concluido` e ele não volta sozinho — fica o
    "refazer o tour" da lateral. Começar sem perguntar trocava a tela inteira
    pelo ensaio logo depois de entrar, sem a pessoa ter pedido nada.
  */
  const convite = Boolean(tour && tour.passo === -1 && !tour.concluido && pathname === "/" && ehLargo());
  const recusar = useCallback(() => gravar({ passo: -1, concluido: true }), [gravar]);

  const informar = useCallback((d: Partial<DadosDoTour>) => setDados((atuais) => ({ ...atuais, ...d })), []);

  const valor = useMemo<ValorDoTour>(
    () => ({
      passo: tour && tour.passo >= 0 && tour.passo < PASSOS.length ? tour.passo : null,
      concluido: (tour ?? PARADO).concluido,
      dados,
      avancarSe,
      entendi,
      pular: parar,
      refazer,
      informar,
      convite,
      recusar,
    }),
    [tour, dados, avancarSe, entendi, parar, refazer, informar, convite, recusar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/** O tour, ou um inerte fora do provedor — telas testadas isoladas não quebram. */
export function useTour(): ValorDoTour {
  return useContext(Contexto) ?? INERTE;
}

const nada = () => undefined;
const INERTE: ValorDoTour = {
  passo: null,
  concluido: false,
  dados: {},
  avancarSe: nada,
  entendi: nada,
  pular: nada,
  refazer: nada,
  informar: nada,
  convite: false,
  recusar: nada,
};

function alvoNaTela(alvo: AlvoDoTour | undefined): boolean {
  return Boolean(alvo) && typeof document !== "undefined" && document.querySelector(`[data-tour="${alvo}"]`) !== null;
}

/** O largo é onde a lateral existe (`--quebra-medio`, 48rem). */
export function ehLargo(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(min-width: 48rem)").matches;
}
